import { getLocalTimeZone, today } from "@internationalized/date";
import { Icon } from "@mindlercare/ui-web";
import { useGetPsychologistIcbtCommunication } from "api/useIcbtQueries";
import { DatePickerWithSchedule } from "components/datePickers/DatePickerWithSchedule";
import { useAssignIcbt } from "../assignIcbtContext";
import { endOfMonth, format, startOfMonth } from "date-fns";
import { useRef, useState } from "react";
import { useButton } from "react-aria";
import { useTranslation } from "react-i18next";
import { styled } from "styled-components";
import { Typography } from "components/typography";

export const FeedbackDatePicker = ({
  value,
  setValue,
  programId,
  setIsSelected,
  patientUserId,
}: {
  value: Date | undefined;
  setValue: (date: Date | undefined) => void;
  programId: string;
  setIsSelected: (arg: boolean) => void;
  patientUserId: string;
}) => {
  const triggerRef = useRef(null);
  const [isDateSelectorOpen, setIsDateSelectorOpen] = useState(false);

  const handleClickSetFeedback = (opened: boolean) => {
    setIsDateSelectorOpen(opened);
    setIsSelected(opened);
  };

  const timeZone = getLocalTimeZone();

  const todayDate = today(timeZone);

  const [focusedMonth, setFocusedMonth] = useState(
    startOfMonth(todayDate.toDate(timeZone)),
  );

  const { state: assignIcbtState } = useAssignIcbt();
  const { programFeedbackDates, programsToAssign } =
    assignIcbtState.assignState;

  // All feedback dates currently in redux (being added) that are not the one we are currently editing
  const newFeedbackDates = Object.values(programsToAssign)
    .flat()
    .map((id) => (programId !== id ? programFeedbackDates?.[id] : null))
    .filter(Boolean) as Date[];

  const { data, isFetching } = useGetPsychologistIcbtCommunication({
    startDate: focusedMonth,
    endDate: endOfMonth(focusedMonth),
  });

  const icbtCommunications = data?.icbtCommunications || [];
  const feedbackDates = newFeedbackDates
    .map((date) => ({ date, patientUserId }))
    .concat(
      icbtCommunications.map(({ feedback_date, patient_user_id }) => ({
        date: new Date(feedback_date),
        patientUserId: patient_user_id,
      })),
    );

  const slots = (data?.slots || [])
    .filter((slot) => slot.status === "BOOKED" || slot.status === "OPEN")
    .map((slot) => ({
      date: new Date(slot.startsAt),
      patientUserId: String(slot.patientUserId),
      isBooked: slot.status === "BOOKED",
    }));

  return (
    <>
      <FeedbackButton
        value={value}
        setIsOpen={handleClickSetFeedback}
        triggerRef={triggerRef}
      />
      <DatePickerWithSchedule
        value={value}
        setValue={(value) => {
          setValue(value);
          setIsDateSelectorOpen(false);
        }}
        isOpen={isDateSelectorOpen}
        setIsOpen={handleClickSetFeedback}
        patientUserId={patientUserId}
        feedbackDates={feedbackDates}
        slotDates={slots}
        isFetching={isFetching}
        focusedMonth={focusedMonth}
        setFocusedMonth={setFocusedMonth}
        triggerRef={triggerRef}
        placement="left"
        offset={-100}
        crossOffset={100}
      />
    </>
  );
};

const FeedbackButton = ({
  value,
  setIsOpen,
  triggerRef,
}: {
  value: Date | undefined;
  setIsOpen: (isOpen: boolean) => void;
  triggerRef: React.RefObject<HTMLButtonElement>;
}) => {
  const { t } = useTranslation();
  const { buttonProps } = useButton(
    {
      onPress: () => setIsOpen(true),
    },
    triggerRef,
  );

  return (
    <FeedbackDateButton {...buttonProps} ref={triggerRef} hasValue={!!value}>
      {value ? (
        <>
          <CheckCircleIcon />
          <Typography insensitive color="SUCCESS" variant="body">
            {t("FEEDBACK_DATE_SELECTED")} {format(value, "eeee, LLL d")}
          </Typography>
          <Icon.Edit />
        </>
      ) : (
        <>
          <Icon.Add />
          <Typography insensitive color="BLACK_GREY" variant="body">
            {t("ADD_FEEDBACK_DATE")}
          </Typography>
        </>
      )}
    </FeedbackDateButton>
  );
};

const CheckCircleIcon = styled(Icon.CheckCircle)`
  color: ${({ theme }) => theme.COLORS.SUCCESS};
`;

const FeedbackDateButton = styled.button<{ hasValue: boolean }>`
  display: flex;
  gap: ${({ theme }) => theme.MARGIN.SM};
  align-items: center;
  border: none;
  border-radius: 4px;
  background: none;
  cursor: pointer;
  color: ${({ hasValue, theme }) =>
    hasValue ? theme.COLORS.SUCCESS : theme.COLORS.GRAY};
  &:hover {
    text-decoration: underline;
  }
`;
